import { MMButton } from '@components'
import { CloseIcon } from '@icons'
import cn from '@integrations/classnames'
import type { MouseEvent, ReactNode } from '@integrations/react'
import { uiSize } from '@utils'
import { isValidFunction, isValidString, tr } from 'mmfintech-commons'
import type { MMButtonProps } from 'mmfintech-portal-commons/types'
import styles from '../OnboardingBanner.module.scss'

interface BannerProps {
  className?: string
  /** Glyph shown inside the round/rounded icon tile. */
  icon: ReactNode
  title?: string
  content: string
  /** Renders the call-to-action button when provided. */
  handleClick?: (() => void) | null
  /** Adds a close button that dismisses the banner without triggering `handleClick`. */
  onDismiss?: () => void
  dataTest?: string
  /** Visible call-to-action caption (e.g. "Issue IBAN"). */
  actionLabel?: string
  /** Optional trailing glyph on the call-to-action. */
  actionIcon?: MMButtonProps['rightIcon']
  /** Click-target hook on the call-to-action, for automation. */
  actionTestId?: string
  dismissTestId?: string
  /** Announcement card tint: `brand` (blue) or `action` (yellow). */
  tone?: 'brand' | 'action'
}

/** Keeps the onboarding banner's gap above the Overview; collapses when empty. */
export function BannerStack({ children }: { children: ReactNode }) {
  return <div className={cn(styles['onboarding-banner-stack'], 'onboarding-banner-stack')}>{children}</div>
}

/**
 * Announcement card with rings, an icon tile, a visible call-to-action and an optional round
 * dismiss chip — onboarding status, dashboard promos, the Accounts IBAN card and the Payment
 * Methods support CTA all render through it.
 */
const Banner = (props: BannerProps) => {
  const {
    className = '',
    icon,
    title = '',
    content,
    handleClick = null,
    onDismiss,
    dataTest,
    actionLabel,
    actionIcon,
    actionTestId,
    dismissTestId,
    tone = 'action'
  } = props

  function handleDismiss(e: MouseEvent) {
    e.stopPropagation()
    onDismiss()
  }

  return (
    <div
      className={cn(
        'onboarding-banner-wrapper',
        styles['announcement'],
        'announcement',
        styles[`announcement--${tone}`],
        `announcement--${tone}`,
        className
      )}
      data-test={dataTest}
    >
      <svg
        width={uiSize(140)}
        height={uiSize(140)}
        className={cn(styles['announcement-rings'], 'announcement-rings')}
        viewBox='0 0 140 140'
        fill='none'
        stroke='currentColor'
        strokeWidth='1.5'
        aria-hidden='true'
      >
        <circle cx='70' cy='70' r='34' />
        <circle cx='70' cy='70' r='52' />
        <circle cx='70' cy='70' r='69' />
      </svg>
      <span className={cn(styles['announcement-icon'], 'announcement-icon')} aria-hidden='true'>
        {icon}
      </span>
      <div className={cn(styles['onboarding-banner-content'], 'onboarding-banner-content')}>
        {isValidString(title) && (
          <div className={cn(styles['onboarding-banner-title'], 'onboarding-banner-title')}>{title}</div>
        )}
        <span className={cn(styles['announcement-text'], 'announcement-text')}>{content}</span>
      </div>
      {isValidFunction(handleClick) && (
        <MMButton
          variant='primary'
          caption={actionLabel}
          rightIcon={actionIcon}
          onClick={handleClick}
          data-test={actionTestId}
        />
      )}
      {isValidFunction(onDismiss) && (
        <button
          type='button'
          className={cn(styles['onboarding-banner-dismiss'], 'onboarding-banner-dismiss')}
          title={tr('FRONTEND.DASHBOARD.PROMO_BANNER.DISMISS', 'Dismiss')}
          aria-label={tr('FRONTEND.DASHBOARD.PROMO_BANNER.DISMISS', 'Dismiss')}
          onClick={handleDismiss}
          data-test={dismissTestId}
        >
          <CloseIcon />
        </button>
      )}
    </div>
  )
}

export default Banner
